"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.ModalContext = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _reactTransitionGroup = require("react-transition-group");

var _events = _interopRequireDefault(require("../../utils/helpers/events"));

var _portal = _interopRequireDefault(require("../portal/portal.style"));

var _modalManager = _interopRequireDefault(require("./__internal__/modal-manager"));

var _modal = require("./modal.style");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function () { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const ModalContext = /*#__PURE__*/_react.default.createContext({});

exports.ModalContext = ModalContext;

const Modal = ({
  children,
  open,
  onCancel,
  disableEscKey,
  disableClose,
  enableBackgroundUI,
  timeout,
  ...rest
}) => {
  const ref = (0, _react.useRef)();
  const listenerAdded = (0, _react.useRef)(false);
  const modalRegistered = (0, _react.useRef)(false);
  const originalOverflow = (0, _react.useRef)(undefined);
  const [isAnimationComplete, setAnimationComplete] = (0, _react.useState)(false);
  const setOverflow = (0, _react.useCallback)(() => {
    if (typeof originalOverflow.current === "undefined" && !enableBackgroundUI) {
      originalOverflow.current = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
    }
  }, [enableBackgroundUI]);
  const unsetOverflow = (0, _react.useCallback)(() => {
    if (typeof originalOverflow.current !== "undefined" && !enableBackgroundUI) {
      document.documentElement.style.overflow = originalOverflow.current;
      originalOverflow.current = undefined;
    }
  }, [enableBackgroundUI]);
  (0, _react.useEffect)(() => {
    if (open) {
      setOverflow();
    }

    if (!open) {
      unsetOverflow();
    }
  }, [open, setOverflow, unsetOverflow]);
  (0, _react.useEffect)(() => {
    return () => {
      unsetOverflow();
    };
  }, [unsetOverflow]);
  const closeModal = (0, _react.useCallback)(ev => {
    const isTopmost = _modalManager.default.isTopmost(ref.current);

    if (onCancel && !disableClose && !disableEscKey && _events.default.isEscKey(ev) && isTopmost) {
      ev.stopImmediatePropagation();
      onCancel(ev);
    }
  }, [disableClose, disableEscKey, onCancel]);
  const addListener = (0, _react.useCallback)(() => {
    /* istanbul ignore else */
    if (!listenerAdded.current) {
      document.addEventListener("keyup", closeModal);
      listenerAdded.current = true;
    }
  }, [closeModal]);
  const removeListener = (0, _react.useCallback)(() => {
    if (listenerAdded.current) {
      document.removeEventListener("keyup", closeModal);
      listenerAdded.current = false;
    }
  }, [closeModal]);
  (0, _react.useEffect)(() => {
    if (open) {
      addListener();
    }

    if (!open) {
      removeListener();
    }
  }, [addListener, open, removeListener]);
  (0, _react.useEffect)(() => {
    return () => {
      removeListener();
    };
  }, [removeListener]);
  const registerModal = (0, _react.useCallback)(() => {
    /* istanbul ignore else */
    if (!modalRegistered.current) {
      _modalManager.default.addModal(ref.current);

      modalRegistered.current = true;
    }
  }, []);
  const unregisterModal = (0, _react.useCallback)(() => {
    if (modalRegistered.current) {
      _modalManager.default.removeModal(ref.current);

      modalRegistered.current = false;
    }
  }, []);
  (0, _react.useEffect)(() => {
    if (open) {
      registerModal();
    }

    if (!open) {
      unregisterModal();
    }
  }, [open, registerModal, unregisterModal]);
  (0, _react.useEffect)(() => {
    return () => {
      unregisterModal();
    };
  }, [unregisterModal]);
  let background;
  let content;

  if (open) {
    background = !enableBackgroundUI ? /*#__PURE__*/_react.default.createElement(_modal.StyledModalBackground, {
      "data-element": "modal-background",
      transitionName: "modal-background"
    }) : null;
    content = children;
  }

  return /*#__PURE__*/_react.default.createElement(_portal.default, null, /*#__PURE__*/_react.default.createElement(_modal.StyledModal, _extends({
    "data-state": open ? "open" : "closed",
    transitionName: "modal",
    ref: ref
  }, rest), /*#__PURE__*/_react.default.createElement(_reactTransitionGroup.TransitionGroup, null, background && /*#__PURE__*/_react.default.createElement(_reactTransitionGroup.CSSTransition, {
    key: "modal",
    appear: true,
    classNames: "modal-background",
    timeout: timeout,
    onEntered: () => setAnimationComplete(true),
    onExiting: () => setAnimationComplete(false)
  }, background)), /*#__PURE__*/_react.default.createElement(_reactTransitionGroup.TransitionGroup, null, content && /*#__PURE__*/_react.default.createElement(_reactTransitionGroup.CSSTransition, {
    appear: true,
    classNames: "modal",
    timeout: timeout
  }, /*#__PURE__*/_react.default.createElement(ModalContext.Provider, {
    value: {
      isAnimationComplete
    }
  }, content)))));
};

Modal.propTypes = {
  /** Modal content */
  children: _propTypes.default.node,

  /** A custom close event handler */
  onCancel: _propTypes.default.func,

  /** Controls the open state of the modal */
  open: _propTypes.default.bool.isRequired,

  /** Determines if the background is disabled when the modal is open */
  enableBackgroundUI: _propTypes.default.bool,

  /** Determines if the Esc Key closes the modal */
  disableEscKey: _propTypes.default.bool,

  /** Determines if the Dialog can be closed */
  disableClose: _propTypes.default.bool,

  /** Transition time */
  timeout: _propTypes.default.number
};
Modal.defaultProps = {
  onCancel: null,
  enableBackgroundUI: false,
  disableEscKey: false,
  timeout: 500
};
var _default = Modal;
exports.default = _default;